import {Effect, Loop} from '@mazechazer/redux-loop'
import * as R from 'ramda'
import {LoginFormAction} from '../Components/LoginForm/LoginFormActions'
import {LoginFormFeedback} from '../Components/LoginForm/LoginFormFeedback'
import {HttpError, sendDownloadRequest, sendRequest} from '../Data/Http'
import * as HttpRequest from '../Data/HttpRequest'
import {IntermediateSession} from '../Data/IntermediateSession'
import * as LocalStorage from '../Data/LocalStorage'
import {update} from '../Data/ObjectHelper'
import * as Result from '../Data/Result'
import {navigate} from '../Data/Browser'
import {Manufacturer} from '../Entities/Manufacturer.entity.evolve'
import {ChooseManufacturerFeedback} from '../Pages/ChooseManufacturer/ChooseManufacturerFeedback'
import {InquiryDetailsFeedback} from '../Pages/InquiryDetails/InquiryDetailsFeedback'
import {InquiryOverviewFeedback} from '../Pages/InquiryOverview/InquiryOverviewFeedback'
import {OrderCreateFeedback} from '../Pages/OrderCreate/OrderCreateFeedback'
import {OrderDetailsFeedback} from '../Pages/OrderDetails/OrderDetailsFeedback'
import {OrderExportCreateFeedback} from '../Pages/OrderExportCreate/OrderExportCreateFeedback'
import {OrderExportOverviewFeedback} from '../Pages/OrderExportOverview/OrderExportOverviewFeedback'
import {OrderOverviewFeedback} from '../Pages/OrderOverview/OrderOverviewFeedback'
import {PasswordChangeFeedback} from '../Pages/PasswordChange/PasswordChangeFeedback'
import {PrismaRequestDetailsFeedback} from '../Pages/PrismaRequestDetails/PrismaRequestDetailsFeedback'
import {PrismaRequestOverviewFeedback} from '../Pages/PrismaRequestOverview/PrismaRequestOverviewFeedback'
import {ProductCalculationDetailsFeedback} from '../Pages/ProductCalculationDetails/ProductCalculationDetailsFeedback'
import {ProductCalculationImportFeedback} from '../Pages/ProductCalculationImport/ProductCalculationImportFeedback'
// tslint:disable-next-line
import {ProductCalculationOverviewFeedback} from '../Pages/ProductCalculationOverview/ProductCalculationOverviewFeedback'
import {Action} from './Actions'
import {
    sendOutHttpDownloadRequest,
    sendOutHttpRequest,
    TaggedDownloadRequest,
    TaggedRequest
} from './HttpManagement'
import * as AuthenticatedRoute from './AuthenticatedRoute'
import * as PublicRoute from './PublicRoute'
import {rootRoute} from './Routing'
import {State} from './State'

export function reduceCreateOrderFeedback(
    feedbackList: OrderCreateFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: OrderCreateFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (newSubAction): Action =>
                                        ({
                                            type: 'OrderCreatePageAction',
                                            subAction: newSubAction
                                        }),
                                ),
                                effects,
                            ),
                    }
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'CreateOrderHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
    )
}

export function reduceOrderCreateExportFeedback(
    feedbackList: OrderExportCreateFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: OrderExportCreateFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({drawerOpen: true}, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'OrderExportCreateHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
                case 'SendDownloadRequest':
                    return sendOutHttpDownloadRequest(
                        feedback.httpRequest,
                        feedback.filename,
                        {
                            type: 'OrderExportCreateHttpDownloadResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
    )
}

export function reduceOrderOverviewExportFeedback(
    feedbackList: OrderExportOverviewFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: OrderExportOverviewFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({drawerOpen: true}, state),
                        effects,
                    }
                case 'SendDownloadRequest':
                    return sendOutHttpDownloadRequest(
                        feedback.httpRequest,
                        feedback.filename,
                        {
                            type: 'OrderExportOverviewHttpDownloadResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
    )
}

export function reducePasswordChangeFeedback(
    feedbackList: PasswordChangeFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: PasswordChangeFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'PasswordChangeResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop)
}

export function reduceOrderDetailsFeedback(
    feedbackList: OrderDetailsFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: OrderDetailsFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (newSubAction): Action =>
                                        ({
                                            type: 'OrderDetailsPageAction',
                                            subAction: newSubAction
                                        })),
                                effects,
                            ),
                    }
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'OrderDetailsHttpResponse',
                            identifier: feedback.identifier,

                        },
                        {state, effects})
                case 'SendDownloadRequest':
                    return sendOutHttpDownloadRequest(
                        feedback.httpRequest,
                        feedback.filename,
                        {
                            type: 'OrderDetailsHttpDownloadResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop)
}

export function reduceOrderOverviewFeedback(
    feedbackList: OrderOverviewFeedback[],
    loop: Loop<State, Action>
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: OrderOverviewFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'OrderOverviewHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (subAction): Action => ({
                                        type: 'OrderOverviewPageAction',
                                        subAction,
                                    })),
                                effects,
                            )
                    }
            }
        },
        loop)
}

export function reduceInquiryOverviewFeedback(
    feedbackList: InquiryOverviewFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'InquiryOverviewHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (subAction): Action => ({
                                        type: 'InquiryOverviewPageAction',
                                        subAction,
                                    })),
                                effects,
                            )
                    }
            }
        },
        loop,
    )
}

export function reduceInquiryDetailsFeedback(
    feedbackList: InquiryDetailsFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return R.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (nextAction): Action => ({
                                        type: 'InquiryDetailsPageAction',
                                        subAction: nextAction,
                                    }),
                                ),
                                effects,
                            ),
                    }
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'InquiryDetailsHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
                case 'SendDownloadRequest':
                    return sendOutHttpDownloadRequest(
                        feedback.httpRequest,
                        feedback.filename,
                        {
                            type: 'InquiryDetailsHttpDownloadResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
        feedbackList,
    )
}

export function reduceLoginFeedback(
    previousLoop: Loop<State, Action>,
    feedback: LoginFormFeedback
): Loop<State, Action> {
    switch (feedback.type) {
        case 'RunEffect':
            return {
                state: previousLoop.state,
                effects:
                    R.append(
                        feedback.effect.map(
                            (subAction: LoginFormAction): Action =>
                                ({type: 'LoginPageAction', subAction})
                        ),
                        previousLoop.effects,
                    )
            }
        case 'LoginSucceeded':
            const {intermediateSession} = feedback
            if (intermediateSession.manufacturers.length === 1) {
                return enterAuthenticatedApp(
                    intermediateSession,
                    intermediateSession.manufacturers[0],
                    previousLoop)
            } else {
                return {
                    state: {
                        type: 'ChoosingManufacturer',
                        page: {
                            type: 'ChoosingManufacturerRedirectPage',
                        },
                        intermediateSession,
                    },
                    effects: previousLoop.effects.concat(
                        navigate<Action>('#/choose-manufacturer', {type: 'NoOp'})),
                }
            }
        case 'PerformLogout':
            return logout(previousLoop)
    }
}

export function reduceProductCalculationOverviewFeedback(
    feedbackList: ProductCalculationOverviewFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return R.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'ProductCalculationOverviewHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
        feedbackList,
    )
}

export function reduceProductCalculationDetailsFeedback(
    feedbackList: ProductCalculationDetailsFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return R.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'ProductCalculationDetailsHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
        feedbackList,
    )
}

export function reduceProductCalculationImportFeedback(
    feedbackList: ProductCalculationImportFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return R.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'ProductCalculationImportHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (subAction): Action => ({
                                        type: 'ProductCalculationImportPageAction',
                                        subAction,
                                    })),
                                effects,
                            )
                    }
            }
        },
        loop,
        feedbackList,
    )
}

export function reducePrismaRequestOverviewFeedback(
    feedbackList: PrismaRequestOverviewFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return R.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'PrismaRequestOverviewHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
        feedbackList,
    )
}

export function reducePrismaRequestDetailsFeedback(
    feedbackList: PrismaRequestDetailsFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return R.reduce(
        ({state, effects}, feedback) => {
            switch (feedback.type) {
                case 'OpenDrawer':
                    return {
                        state: update({
                            drawerOpen: true,
                        }, state),
                        effects,
                    }
                case 'SendRequest':
                    return sendOutHttpRequest(
                        feedback.httpRequest,
                        {
                            type: 'PrismaRequestDetailsHttpResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
                case 'SendDownloadRequest':
                    return sendOutHttpDownloadRequest(
                        feedback.httpRequest,
                        feedback.filename,
                        {
                            type: 'PrismaRequestDetailsHttpDownloadResponse',
                            identifier: feedback.identifier,
                        },
                        {state, effects})
            }
        },
        loop,
        feedbackList,
    )
}

export function reduceReloginFeedback(
    feedbackList: LoginFormFeedback[],
    loop: Loop<State, Action>,
): Loop<State, Action> {
    return feedbackList.reduce(
        (
            {state, effects}: Loop<State, Action>,
            feedback: LoginFormFeedback
        ): Loop<State, Action> => {
            switch (feedback.type) {
                case 'RunEffect':
                    return {
                        state,
                        effects:
                            R.append(
                                feedback.effect.map(
                                    (subAction): Action => ({
                                        type: 'ReloginModalAction',
                                        subAction,
                                    })),
                                effects,
                            )
                    }
                case 'LoginSucceeded':
                    if (state.type === 'Authenticated' && state.relogin !== null) {
                        const failedPageLoadRequest = state.relogin.failedPageLoadRequest
                        const
                            {intermediateSession} = feedback,
                            {token} = intermediateSession.authentication,
                            newSession = update({
                                authentication: update({
                                    token,
                                }, state.session.authentication),
                            }, state.session),
                            pageLoadRetries =
                                failedPageLoadRequest !== null ?
                                    [
                                        sendRequest(
                                            (result): Action => ({
                                                type: 'PageLoadResponse',
                                                httpRequest: failedPageLoadRequest.httpRequest,
                                                identifier: failedPageLoadRequest.identifier,
                                                result,
                                            }),
                                            HttpRequest.authorize(
                                                token,
                                                failedPageLoadRequest.httpRequest
                                            ),
                                        )
                                    ] :
                                    [],
                            httpRequestRetries = R.map(
                                (failedRequest: TaggedRequest): Effect<Action> => {
                                    const {httpRequest, receiver} = failedRequest
                                    return sendRequest(
                                        (nextResult: Result.Result<HttpError, string>): Action => ({
                                            type: 'HttpResponse',
                                            httpRequest,
                                            receiver,
                                            result: nextResult,
                                        }),
                                        HttpRequest.authorize(token, httpRequest))
                                },
                                state.relogin.failedRequests),
                            httpDownladRequestRetries = R.map(
                                (failedDownloadRequest: TaggedDownloadRequest): Effect<Action> => {
                                    const {httpRequest, filename, receiver} = failedDownloadRequest
                                    return sendDownloadRequest(
                                        (nextResult: Result.Result<HttpError, null>): Action => ({
                                            type: 'HttpDownloadResponse',
                                            httpRequest,
                                            filename,
                                            receiver,
                                            result: nextResult,
                                        }),
                                        HttpRequest.authorize(token, httpRequest),
                                        filename)
                                },
                                state.relogin.failedDownloadRequests),
                            allRetries =
                                R.concat(
                                    R.concat(pageLoadRetries, httpRequestRetries),
                                    httpDownladRequestRetries,
                                )
                        return {
                            state: update({
                                relogin: null,
                                session: newSession,
                                runningPageLoads:
                                    state.relogin.failedPageLoadRequest !== null
                                        ? state.runningPageLoads + 1
                                        : state.runningPageLoads,
                            }, state),
                            effects:
                                R.append(
                                    LocalStorage.set<Action>(
                                        'session',
                                        JSON.stringify(newSession),
                                        {type: 'NoOp'}),
                                    allRetries),
                        }
                    } else {
                        return {state, effects}
                    }
                case 'PerformLogout':
                    return logout({state, effects})
            }
        },
        loop)
}

export function chooseManufacturerFeedbackReducer(
    intermediateSession: IntermediateSession,
    previousLoop: Loop<State, Action>,
    feedback: ChooseManufacturerFeedback
): Loop<State, Action> {
    switch (feedback.type) {
        case 'ManufacturerChosen':
            return enterAuthenticatedApp(
                intermediateSession,
                feedback.manufacturer,
                previousLoop)
    }
}

function enterAuthenticatedApp(
    intermediateSession: IntermediateSession,
    manufacturer: Manufacturer,
    previousLoop: Loop<State, Action>
): Loop<State, Action> {
    const session = {
        authentication: intermediateSession.authentication,
        cis2User: intermediateSession.cis2User,
        manufacturer: manufacturer,
    }
    return {
        state: {
            type: 'Authenticated',
            relogin: null,
            session,
            page: {type: 'AuthenticatedRedirectPage'},
            pageLoad: {type: 'Default'},
            runningPageLoads: 0,
            loadingIndicator: false,
            drawerOpen: false,
        },
        effects: previousLoop.effects.concat(
            navigate<Action>(
                AuthenticatedRoute.url(rootRoute(session.manufacturer)),
                {type: 'NoOp'},
            ),
            LocalStorage.set<Action>(
                'session',
                JSON.stringify(session),
                {type: 'NoOp'})),
    }
}

export function logout({state, effects}: Loop<State, Action>): Loop<State, Action> {
    if (state.type === 'Authenticated') {
        return {
            state: {
                type: 'Public',
                page: {
                    type: 'PublicRedirectPage',
                },
            },
            effects: effects.concat(
                navigate<Action>(
                    PublicRoute.url({type: 'Login'}),
                    {type: 'NoOp'},
                ),
                LocalStorage.remove<Action>('session', {type: 'NoOp'})),
        }
    } else {
        return {state, effects}
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/App/FeedbackProcessing.ts